import { useEffect } from 'react';
import { createHighlightRect } from './pageElements';
import type { PdfDisplayMode } from './types';
import type { PdfViewerState } from './usePdfViewerState';

interface UseHighlightLayersArgs {
  state: PdfViewerState;
  displayMode: PdfDisplayMode;
  scrollToMatch: (
    page: number,
    pdfRect?: [number, number, number, number],
    behavior?: ScrollBehavior,
  ) => void;
}

/**
 * Repaints the match rectangles from the stored PDF coordinates, so zooming
 * moves the highlights without re-running the search.
 */
export function useHighlightLayers({ state, displayMode, scrollToMatch }: UseHighlightLayersArgs) {
  const {
    pdfDoc, scale, currentPage, currentResultIndex, pdfSearchResults,
    pageRenderComplete, renderedPages, setCurrentResultIndex, highlightLayerRefs,
  } = state;

  useEffect(() => {
    let isCancelled = false;

    const renderHighlightsForPage = async (pageNum: number) => {
      const highlightLayer = highlightLayerRefs.current.get(pageNum);
      if (!highlightLayer || !pdfDoc) return;

      highlightLayer.innerHTML = '';

      const pageResults = pdfSearchResults.filter(r => r.page === pageNum);
      if (pageResults.length === 0) return;

      const page = await pdfDoc.getPage(pageNum);
      if (isCancelled) return;

      const viewport = page.getViewport({ scale });

      pageResults.forEach((result) => {
        const globalIndex = pdfSearchResults.findIndex(
          r => r.page === result.page &&
               r.pdfRect[0] === result.pdfRect[0] &&
               r.pdfRect[1] === result.pdfRect[1] &&
               r.pdfRect[2] === result.pdfRect[2] &&
               r.pdfRect[3] === result.pdfRect[3]
        );

        // #1304: one rect per line. A single box spanning them would paint the
        // gap between two shapes on a converted slide.
        for (const pdfRect of result.pdfRects) {
          const viewRect = viewport.convertToViewportRectangle(pdfRect);

          highlightLayer.appendChild(createHighlightRect({
            left: Math.min(viewRect[0], viewRect[2]),
            top: Math.min(viewRect[1], viewRect[3]),
            width: Math.abs(viewRect[0] - viewRect[2]),
            height: Math.abs(viewRect[1] - viewRect[3]),
            isCurrent: globalIndex === currentResultIndex,
            onSelect: () => {
              setCurrentResultIndex(globalIndex);
              if (displayMode === 'scroll') {
                scrollToMatch(result.page, result.pdfRect, 'smooth');
              }
            },
          }));
        }
      });
    };

    const renderHighlights = async () => {
      if (!pageRenderComplete) return;

      if (displayMode === 'scroll') {
        const pagesToHighlight = Array.from(renderedPages);
        for (const pageNum of pagesToHighlight) {
          if (isCancelled) break;
          await renderHighlightsForPage(pageNum);
        }
      } else {
        await renderHighlightsForPage(currentPage);
      }
    };

    renderHighlights();

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs and setters are stable
  }, [
    displayMode, pdfSearchResults, currentPage, currentResultIndex, pdfDoc, scale,
    pageRenderComplete, renderedPages, scrollToMatch,
  ]);
}
